/*
 * InterviewSetup — "Start AI Interview" / "Start Written Interview" on ONE page
 * (#/interviews/setup), with progressive disclosure:
 *
 *   Your goal (role · level)        — pre-filled from the learner's goal; Change reveals the role picker
 *   Interview basics                — format (video / voice), role, level, company (optional)
 *   Focus areas                     — AI-recommended topics for THIS role, each with its reason;
 *                                     Customize reveals the role's competency → skill → topic tree
 *   Other settings                  — difficulty, questions (+ estimated time), personalization
 *   Your interview (sticky)         — the summary + Start Interview
 *
 * Role drives everything: focus options only ever come from the role's hub
 * scope, so an invalid role/topic combination can't be built. Recommendations
 * use real signals only (memory tree, due reviews, the last interview for this
 * role) and fall back to the role's core topics.
 */
import React, { useEffect, useMemo, useState } from 'react';
import APIService from '../../services/APIService';
import IvShell from './InterviewChrome';
import { loadGoal } from './HomeDashboard';
import { indexTree } from './paths';
import { loadPrefs } from './settingsPrefs';
import {
  AI_COUNTS, COMPANIES, DEFAULT_SETUP, DIFFICULTIES, LENGTHS, LEVELS, ROLES, ROLE_CATEGORIES, TYPES,
  diffLabel, focusOptions, isAiType, recommendFocus, roleForGoal, roleOf,
} from './interviewData';
import { IconArrowRight, IconBrain, IconCheck, IconChevDown, IconChevRight, IconSpark } from './icons';

const OTHER = '__other__';

const InterviewSetup = ({ initial, hub, history = [], onCancel, onStart, ...shell }) => {
  const written = initial?.type === 'written';
  const goalRole = roleForGoal(loadGoal());
  const prefs = loadPrefs();
  const [s, setS] = useState(() => {
    const base = { ...DEFAULT_SETUP, ...(initial || {}) };
    // the learner's goal decides the role unless the caller asked for one
    if (!initial?.roleId && goalRole) base.roleId = goalRole.id;
    if (!initial?.level && (prefs.experienceLevel || prefs.interviewLevel)) base.level = prefs.experienceLevel || prefs.interviewLevel;
    return base;
  });
  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const [changingRole, setChangingRole] = useState(!goalRole);
  const [companyPick, setCompanyPick] = useState(() => (!s.company ? '' : COMPANIES.includes(s.company) ? s.company : OTHER));
  const [customize, setCustomize] = useState(false);
  const [openSkill, setOpenSkill] = useState(null);
  const [mem, setMem] = useState(null);
  const [due, setDue] = useState([]);
  const [touchedFocus, setTouchedFocus] = useState(!!(initial?.focus && initial.focus.length));

  useEffect(() => {
    APIService.getMemoryTree().then((d) => setMem(d.tree || [])).catch(() => setMem([]));
    APIService.getEngineMemory().then((d) => setDue(d?.due_reviews || [])).catch(() => setDue([]));
  }, []);

  const role = roleOf(s.roleId);
  const startable = !!role && role.rootIds.length > 0;
  const byId = useMemo(() => indexTree(hub || []), [hub]);
  const memIdx = useMemo(() => indexTree(mem || []), [mem]);
  const options = useMemo(() => focusOptions(role, hub), [role, hub]);
  const recs = useMemo(() => (mem ? recommendFocus({ role, hub, memTree: mem, due, history }) : []), [role, hub, mem, due, history]);

  // Pre-select the recommendations once they're known (until the learner edits focus).
  useEffect(() => {
    if (touchedFocus || !recs.length) return;
    setS((p) => ({ ...p, focus: recs.map(({ id, name, reason }) => ({ id, name, reason })), recommended: false }));
  }, [recs, touchedFocus]);

  const pickRole = (id) => { setTouchedFocus(false); set({ roleId: id, focus: [], recommended: true }); setOpenSkill(null); };
  const has = (id) => s.focus.some((f) => f.id === id);
  const toggle = (o) => {
    setTouchedFocus(true);
    setS((p) => {
      const on = p.focus.some((f) => f.id === o.id);
      const focus = on ? p.focus.filter((f) => f.id !== o.id) : [...p.focus, { id: o.id, name: o.name, reason: o.reason }];
      return { ...p, focus, recommended: focus.length === 0 };
    });
  };
  const useRecommended = () => { setTouchedFocus(true); set({ focus: recs.map(({ id, name, reason }) => ({ id, name, reason })), recommended: false }); };
  const usingRecs = recs.length > 0 && s.focus.length === recs.length && recs.every((r) => has(r.id));

  // Customize: competency (hub root) → skill (topic) → topic (subtopic)
  const competencies = useMemo(() => {
    const groups = {};
    options.forEach((o) => {
      const root = String(o.id).split('.')[0];
      groups[root] = groups[root] || { id: root, name: byId[root]?.name || root, skills: [] };
      groups[root].skills.push(o);
    });
    return Object.values(groups);
  }, [options, byId]);

  // Personalization summary from the learner's memory, within this role's scope
  const inScope = (id) => options.some((o) => id === o.id || String(id).startsWith(`${o.id}.`));
  const persona = useMemo(() => {
    const topics = options.map((o) => memIdx[o.id]).filter(Boolean);
    return {
      weak: topics.filter((t) => t.attempts > 0 && (t.status === 'weak' || (t.mastery || 0) < 0.5)).length,
      recent: topics.filter((t) => t.attempts > 0 && (t.last_seen_days ?? 99) <= 7).length,
      overdue: due.filter((d) => inScope(d.concept_id)).length,
    };
  }, [options, memIdx, due]);   // eslint-disable-line react-hooks/exhaustive-deps

  const length = LENGTHS.find((l) => l.id === s.length) || LENGTHS[1];
  const count = written ? length.questions : s.questions;
  const minutes = written ? length.minutes : (AI_COUNTS.find((c) => c.n === s.questions)?.minutes || 20);
  const summary = [
    role?.title, s.level, s.company || null,
    written ? 'Written' : (TYPES.find((t) => t.id === s.type)?.label || ''),
    diffLabel(s.difficulty), `${count} questions`, `~${minutes} min`,
  ].filter(Boolean);

  const roleSelect = (
    <label className="kh-select ivs-select">
      <span className="pu-sr">Role</span>
      <select value={s.roleId} onChange={(e) => pickRole(e.target.value)} aria-label="Role">
        {ROLE_CATEGORIES.filter(([id]) => id !== 'popular').map(([cid, cl]) => (
          <optgroup key={cid} label={cl}>
            {ROLES.filter((r) => r.cats.filter((c) => c !== 'popular')[0] === cid).map((r) => (
              <option key={r.id} value={r.id} disabled={!r.rootIds.length}>{r.title}{r.rootIds.length ? '' : ' — coming soon'}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <IconChevDown size={14} />
    </label>
  );

  return (
    <IvShell hub={hub} className="ivs-page" {...shell}>
      <section className="ivx-head">
        <div>
          <button type="button" className="pu-link ivx-back" onClick={onCancel}>← Interviews</button>
          <h1>{written ? 'Start Written Interview' : 'Start AI Interview'}</h1>
          <p>{written ? 'Practise structuring strong answers for the role you’re preparing for.' : 'Customise your interview to make it relevant to the role you’re preparing for.'}</p>
        </div>
      </section>

      <div className="ivs2">
        {/* Goal */}
        <section className="pu-panel ivs2-goal">
          <span className="ivs2-goal-icon" aria-hidden="true"><IconBrain size={18} /></span>
          <div className="ivs2-goal-body">
            <small>{goalRole && !changingRole ? 'Your current goal' : 'What role are you preparing for?'}</small>
            {!changingRole ? <b>{role?.title} · {s.level}</b> : roleSelect}
          </div>
          {!changingRole && <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => setChangingRole(true)}>Change</button>}
        </section>

        {/* Basics */}
        <section className="pu-panel ivs2-sec" aria-labelledby="ivs2-basics">
          <h2 id="ivs2-basics"><span className="ivs2-n">1</span>Interview basics</h2>
          {!written && (
            <div className="ivs2-row">
              <span className="ivs2-k">Format</span>
              <div className="ivs-types" role="radiogroup" aria-label="Interview format">
                {TYPES.filter((t) => isAiType(t.id)).map((t) => (
                  <button key={t.id} type="button" role="radio" aria-checked={s.type === t.id}
                    className={`ivs-type${s.type === t.id ? ' on' : ''}`} onClick={() => set({ type: t.id })}>
                    <span className="ivs-radio" aria-hidden="true" />
                    <span><b>{t.label}{t.id === 'video' && <em className="ivs2-tag">Recommended</em>}</b><small>{t.id === 'video' ? 'Camera + voice' : 'Audio only'}</small></span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {changingRole || !goalRole ? null : (
            <div className="ivs2-row"><span className="ivs2-k">Role</span>{roleSelect}</div>
          )}
          <div className="ivs2-row">
            <span className="ivs2-k">Level</span>
            <div className="pg-seg ivs-seg" role="radiogroup" aria-label="Level">
              {LEVELS.map((l) => <button key={l} type="button" role="radio" aria-checked={s.level === l} className={s.level === l ? 'on' : ''} onClick={() => set({ level: l })}>{l}</button>)}
            </div>
          </div>
          <div className="ivs2-row">
            <span className="ivs2-k">Company <em>(optional)</em></span>
            <div className="ivs2-company">
              <div className="ivs-company">
                <label className="kh-select ivs-select">
                  <span className="pu-sr">Company</span>
                  <select value={companyPick} aria-label="Company (optional)"
                    onChange={(e) => { const v = e.target.value; setCompanyPick(v); set({ company: v === OTHER ? '' : v }); }}>
                    <option value="">No specific company</option>
                    {COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    <option value={OTHER}>Other…</option>
                  </select>
                  <IconChevDown size={14} />
                </label>
                {companyPick === OTHER && <input className="ivs-input" value={s.company} onChange={(e) => set({ company: e.target.value })} placeholder="Company name" aria-label="Company name" />}
              </div>
              {s.company && <small className="ivs2-hint">Saved with this interview for your history. Questions are role-based — company-specific question styles aren’t available yet.</small>}
            </div>
          </div>
        </section>

        {/* Focus areas */}
        <section className="pu-panel ivs2-sec" aria-labelledby="ivs2-focus">
          <div className="ivs2-sec-head">
            <h2 id="ivs2-focus"><span className="ivs2-n">2</span>Focus areas</h2>
            <button type="button" className="pu-link" aria-expanded={customize} onClick={() => setCustomize((v) => !v)}>
              {customize ? 'Hide topics' : 'Customize'} <IconChevDown size={12} />
            </button>
          </div>
          <p className="ivs2-sub">Choose what you want to practise. We recommend areas based on your role and learning history.</p>

          {mem === null ? <p className="pu-empty">Reading your learning memory…</p> : recs.length > 0 && (
            <div className="ivs2-ai">
              <div className="ivs2-ai-head">
                <span><IconSpark size={14} /> AI recommendation</span>
                <button type="button" className={`pu-btn pu-btn-sm ${usingRecs ? 'pu-btn-quiet' : 'pu-btn-primary'}`} onClick={useRecommended} disabled={usingRecs}>
                  {usingRecs ? <><IconCheck size={13} /> Using recommended focus</> : 'Use recommended focus'}
                </button>
              </div>
              <p>Based on your {persona.weak || persona.recent || persona.overdue ? 'recent practice' : 'role'}, focus on {recs.map((r) => r.name).join(', ').replace(/, ([^,]*)$/, ' and $1')}.</p>
              <ul className="ivs2-recs">
                {recs.map((r) => (
                  <li key={r.id}>
                    <button type="button" className={has(r.id) ? 'on' : ''} aria-pressed={has(r.id)} onClick={() => toggle(r)}>
                      <span className="ivs2-check" aria-hidden="true">{has(r.id) && <IconCheck size={11} />}</span>
                      <span><b>{r.name}</b><small>{r.reason}</small></span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {customize && (
            <div className="ivs2-tree">
              {competencies.map((c) => (
                <div key={c.id} className="ivs2-comp">
                  <span className="ivs2-comp-name">{c.name}</span>
                  <ul>
                    {c.skills.map((sk) => {
                      const kids = byId[sk.id]?.children || [];
                      const open = openSkill === sk.id;
                      return (
                        <li key={sk.id}>
                          <div className="ivs2-skill">
                            <button type="button" className={`ivs2-chip${has(sk.id) ? ' on' : ''}`} aria-pressed={has(sk.id)} onClick={() => toggle(sk)}>
                              {has(sk.id) && <IconCheck size={11} />}{sk.name}
                            </button>
                            {kids.length > 0 && (
                              <button type="button" className="ivs2-expand" aria-expanded={open} aria-label={`${open ? 'Hide' : 'Show'} topics in ${sk.name}`} onClick={() => setOpenSkill(open ? null : sk.id)}>
                                {open ? <IconChevDown size={12} /> : <IconChevRight size={12} />}{kids.length} topic{kids.length === 1 ? '' : 's'}
                              </button>
                            )}
                          </div>
                          {open && (
                            <div className="ivs2-topics">
                              {kids.map((t) => (
                                <button key={t.id} type="button" className={`ivs2-chip sm${has(t.id) ? ' on' : ''}`} aria-pressed={has(t.id)} onClick={() => toggle({ id: t.id, name: t.name })}>
                                  {has(t.id) && <IconCheck size={11} />}{t.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <p className="ivs2-foot">
            {s.focus.length
              ? <>Focusing on <b>{s.focus.map((f) => f.name).join(', ')}</b>. <button type="button" className="pu-link" onClick={() => { setTouchedFocus(true); set({ focus: [], recommended: true }); }}>Clear</button></>
              : <>No focus picked — the interview covers all {role?.title} topics.</>}
          </p>
        </section>

        {/* Other settings */}
        <section className="pu-panel ivs2-sec" aria-labelledby="ivs2-other">
          <h2 id="ivs2-other"><span className="ivs2-n">3</span>Other settings</h2>
          <div className="ivs2-row">
            <span className="ivs2-k">Difficulty</span>
            <div>
              <div className="pg-seg ivs-seg" role="radiogroup" aria-label="Difficulty">
                {DIFFICULTIES.map(([v, l]) => <button key={l} type="button" role="radio" aria-checked={s.difficulty === v} className={s.difficulty === v ? 'on' : ''} onClick={() => set({ difficulty: v })}>{l}</button>)}
              </div>
              <small className="ivs2-hint">{s.difficulty == null ? 'Difficulty adjusts based on your answers.' : written ? `Every question is pitched at ${diffLabel(s.difficulty).toLowerCase()}.` : 'Saved with the interview — the AI interviewer adapts to your answers.'}</small>
            </div>
          </div>
          <div className="ivs2-row">
            <span className="ivs2-k">Questions</span>
            <div className="ivs2-count">
              <div className="pg-seg ivs-seg" role="radiogroup" aria-label="Number of questions">
                {written
                  ? LENGTHS.map((l) => <button key={l.id} type="button" role="radio" aria-checked={s.length === l.id} className={s.length === l.id ? 'on' : ''} onClick={() => set({ length: l.id })}>{l.questions}</button>)
                  : AI_COUNTS.map(({ n, available }) => (
                    <button key={n} type="button" role="radio" aria-checked={s.questions === n} disabled={!available}
                      title={available ? undefined : 'Longer AI interviews aren’t available yet'} className={s.questions === n ? 'on' : ''} onClick={() => set({ questions: n })}>{n}</button>
                  ))}
              </div>
              <small className="ivs2-hint">{count} questions · ~{minutes} min</small>
            </div>
          </div>
          {!written && (
            <label className={`ivs-personal${s.personalize ? ' on' : ''}`}>
              <input type="checkbox" checked={s.personalize} onChange={(e) => set({ personalize: e.target.checked })} />
              <span>
                <b>Personalise my interview</b>
                <small>Use my practice history, weak areas, goals and learning memory to tailor this interview.</small>
                {mem && (persona.weak + persona.recent + persona.overdue > 0) && (
                  <span className="ivs2-persona">
                    {persona.weak > 0 && <em>{persona.weak} weak area{persona.weak === 1 ? '' : 's'}</em>}
                    {persona.recent > 0 && <em>{persona.recent} recently practised</em>}
                    {persona.overdue > 0 && <em>{persona.overdue} overdue for review</em>}
                  </span>
                )}
              </span>
            </label>
          )}
        </section>
      </div>

      {/* Sticky summary */}
      <div className="ivs2-bar" role="region" aria-label="Your interview">
        <div className="ivs2-bar-sum">
          <small>Your interview</small>
          <span>{summary.map((x, i) => <React.Fragment key={x}>{i > 0 && <i aria-hidden="true">·</i>}{x}</React.Fragment>)}</span>
        </div>
        <button type="button" className="pu-btn pu-btn-quiet" onClick={onCancel}>Cancel</button>
        <button type="button" className="pu-btn pu-btn-primary" disabled={!startable} onClick={() => onStart(s)}>
          {written ? 'Start Written Interview' : 'Start Interview'} <IconArrowRight size={14} />
        </button>
      </div>
    </IvShell>
  );
};

export default InterviewSetup;
